/**
 * One document as a card: a preview of the page, its name, and what can be done to it.
 *
 * THE THUMBNAIL IS THE DOCUMENT, not a picture of it. Google renders page images, which needs a
 * rendering pipeline, somewhere to keep the images, and something to notice when a document changes
 * and throw the old one away. The text is already here and cannot go stale -- it IS the body -- so
 * the card draws a miniature of the opening instead. It shows words rather than layout, which for a
 * procedure is the half that tells you whether it is the one you wanted.
 */
import React from "react";
import {
    Box, Card, CardActionArea, Chip, IconButton, ListItemIcon, ListItemText,
    Menu, MenuItem, Tooltip, Typography,
} from "@mui/material";
import { MoreVert, DescriptionOutlined } from "@mui/icons-material";

import { REVISION_ACTIONS } from "../Common/LifecycleActions";
import { statusPill } from "../../util/statusPill";
import { Grey5, Grey17 } from "../../theme/styles";
import { parts } from "../../util/documentSearch";

export type DocumentRow = {
    USID: string;
    ObjectID?: string;
    Name?: string;
    DisplayRevision?: string;
    Status?: string;
    // The TYPE's acronym and name, joined through OBJECT to OBJECT_TYPE by QMS. The acronym is what
    // the Filter By band filters on, because it is what the object number is built from.
    ObjectTypeAcronym?: string;
    ObjectTypeName?: string;
    CreatedDatetime?: string;
    LastModifiedDatetime?: string;
    Preview?: string;
    /** The matched sentence from a search, with the server's <b> markers still on it. */
    Snippet?: string;
    /** Whether the document contains a figure, so the card knows to ask for a picture of it. */
    HasFigure?: boolean;
    /** The part the preview and the picture are both of. */
    FirstPartUSID?: string | null;
    HasBody?: boolean;
    OpenSuggestions?: number;
    /** How many ParkourAI Docs this revision carries. The card previews the first of them. */
    DocumentCount?: number;
};

export default function DocumentCard({ row, onOpen, onAction, busy, thumbnailFor }: {
    row: DocumentRow;
    onOpen: () => void;
    onAction: (action: string) => void;
    busy?: string | null;
    /**
     * The address of a picture of page one, fetched by the page.
     *
     * THE NETWORK STAYS IN THE PAGE. The endpoint answers with a presigned address rather than the
     * image itself -- it is a download like the PDF, never bytes through the Lambda -- so an <img
     * src> cannot point at it, and the card is not the place to hold an axios instance.
     */
    thumbnailFor?: (partUSID: string) => Promise<string | null>;
}) {
    const [menu, setMenu] = React.useState<HTMLElement | null>(null);
    const [picture, setPicture] = React.useState<string | null>(null);

    // ASKED FOR ONCE, AND ONLY WHEN THERE IS SOMETHING TO SEE. A document with no figure keeps the
    // text miniature, which is already a good likeness of a page of prose; drawing a picture is a
    // render and a store, and doing it for a whole list would be a page of renders nobody needed.
    const wantsPicture = !!(row.HasBody && row.HasFigure && row.FirstPartUSID && !row.Snippet);
    React.useEffect(() => {
        let alive = true;
        if (!wantsPicture || !thumbnailFor || !row.FirstPartUSID) { setPicture(null); return; }
        thumbnailFor(row.FirstPartUSID)
            .then(url => { if (alive) setPicture(url); })
            .catch(() => { if (alive) setPicture(null); });
        return () => { alive = false; };
    }, [wantsPicture, thumbnailFor, row.FirstPartUSID]);

    // THE SAME TABLE t1 AND t5 USE, keyed on the same status. Which moves exist is display only --
    // the server refuses an illegal transition whatever this offers -- but a card that offered
    // Revise on a draft would be teaching people a lifecycle that is not the real one.
    const actions = REVISION_ACTIONS[row.Status ?? ""] ?? [];

    return (
        <Card variant="outlined" sx={{ width: 190, borderRadius: 2, overflow: "hidden" }}>
            <CardActionArea onClick={onOpen}>
                <Box sx={{
                    height: 196, backgroundColor: "#FFFFFF", display: "flex", flexDirection: "column",
                    borderBottom: "1px solid", borderColor: "divider", overflow: "hidden",
                    // RELATIVE, so the parts badge can sit on the corner of the preview.
                    position: "relative",
                }}>
                    {/* HOW MANY MORE PARTS THERE ARE. The preview is the FIRST part; a card showing
                        one of six with no hint that five exist misrepresents the document.

                        ON THE CORNER OF THE PREVIEW, not in the footer. Down there it sat between
                        the revision and the menu and pushed both sideways -- on a 190px card that
                        is the row that has least to give, and "Rev 1" ended up wrapped under a
                        chip that is about something else entirely. A badge belongs on the thing it
                        counts, and the thing it counts is the page underneath it. */}
                    {(row.DocumentCount ?? 0) > 1 && (
                        <Tooltip title={`${row.DocumentCount} documents on this revision`}>
                            <Box sx={{
                                position: "absolute", top: 6, right: 6, zIndex: 1,
                                minWidth: 20, height: 20, px: 0.5, borderRadius: "10px",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 11, fontWeight: 500, lineHeight: 1,
                                // OPAQUE, because it sits over the page: a translucent badge over
                                // a line of the miniature reads as neither.
                                backgroundColor: "rgba(32,33,36,0.72)", color: "#FFFFFF",
                            }}>
                                {`+${(row.DocumentCount ?? 1) - 1}`}
                            </Box>
                        </Tooltip>
                    )}
                    <Box sx={{
                        flexGrow: 1, p: 1.25, overflow: "hidden",
                        // THE PREVIEW STARTS BELOW THE BADGE WHEN THERE IS ONE, and keeps its own
                        // padding when there is not. A badge sitting on the first line of the
                        // miniature was legible -- it is opaque -- but it was still a number
                        // printed over a sentence, and on a card whose whole job is to look like
                        // the page it is a card that looks like the page with something on it.
                        //
                        // CONDITIONAL, because this costs 18px of preview: paying that on every
                        // single-part document in the tenant, for a badge those cards do not draw,
                        // would be the wrong trade in the common case.
                        pt: (row.DocumentCount ?? 0) > 1 ? "26px" : undefined,
                        // The miniature. Real text at a size you read shape from rather than words --
                        // which is exactly what a thumbnail is for.
                        fontSize: "5.5px", lineHeight: 1.45, color: Grey17, whiteSpace: "pre-wrap",
                    }}>
                        {/* A PICTURE WHEN THERE IS SOMETHING TO SEE. The text miniature below is
                            a good likeness of a page of prose and a poor one of a page with a
                            diagram on it -- which read identically here until now, both as an
                            almost empty tile. Asked for only when the document HAS a figure,
                            because drawing one is a render and a store.

                            NOT WHILE SEARCHING. A search result's job is to show why it matched,
                            which is the sentence, not the first page. */}
                        {picture
                            ? <Box component="img" src={picture} alt=""
                                   // A PICTURE THAT WILL NOT LOAD FALLS BACK TO THE WORDS rather
                                   // than leaving a broken-image glyph on the card.
                                   onError={() => setPicture(null)}
                                   sx={{ width: "100%", display: "block" }}/>
                            : null}
                        <Box sx={{ display: picture ? "none" : "block" }}>
                        {row.HasBody && (row.Snippet || row.Preview)
                            ? (row.Snippet
                                // THE MATCH, EMPHASISED. Rendered from runs rather than as HTML:
                                // ts_headline leaves the document's own text unescaped, so markup
                                // somebody typed in the editor would otherwise be markup here.
                                ? parts(row.Snippet).map((part, at) => (
                                    <Box key={at} component="span"
                                         sx={part.hit ? { backgroundColor: "#FFF3B0", fontWeight: 700 } : undefined}>
                                        {part.text}
                                    </Box>))
                                : row.Preview)
                            : (
                                <Box sx={{ height: "100%", display: "flex", flexDirection: "column",
                                           alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                                    <DescriptionOutlined sx={{ fontSize: 28, color: "#C9CBD2" }} />
                                    <Typography sx={{ fontSize: 10, color: Grey5 }}>No content yet</Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* THE OBJECT ID, ON THE PAGE ITSELF rather than in the caption below it.
                        SOP-00011 is how people refer to a controlled document out loud and in every
                        other system; the name is how they recognise it. Putting the number at the
                        foot of the tile is where a controlled document carries it -- in the footer
                        of the printed page -- and it leaves the caption to the name, which is
                        already truncating at this width.

                        CENTRED, and the strip is drawn whether or not there is a number: an object
                        awaiting one from its type's pool would otherwise make its tile a different
                        height from the one beside it. */}
                    <Box sx={{ flexShrink: 0, py: 0.5, px: 1, textAlign: "center",
                               borderTop: "1px solid", borderColor: "#EDEFF3" }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.3,
                                          color: Grey5, overflow: "hidden",
                                          textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {row.ObjectID || "\u2014"}
                        </Typography>
                    </Box>
                </Box>
            </CardActionArea>

            <Box sx={{ px: 1.25, pt: 1, pb: 0.5 }}>
                <Tooltip title={row.Name || ""}>
                    <Typography sx={{ fontSize: 13, fontWeight: 500, color: Grey17,
                                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {row.Name || "Untitled"}
                    </Typography>
                </Tooltip>
            </Box>

            <Box sx={{ px: 1, pb: 0.75, display: "flex", alignItems: "center", gap: 0.75 }}>
                {/* THE SAME PILL THE LIST DRAWS, from the same palette. Painted by hand here, a
                    card said DRAFT in filled blue and SUPERSEDED, CANCELLED, DEPRECATED and VOID
                    in one identical grey outline -- four states that mean four different things,
                    and one of them is still live. */}
                {statusPill(row.Status)}
                <Typography sx={{ fontSize: 11, color: Grey5 }}>
                    {row.DisplayRevision ? `Rev ${row.DisplayRevision}` : ""}
                </Typography>
                {!!row.OpenSuggestions && (
                    <Tooltip title={`${row.OpenSuggestions} tracked change(s) to resolve before this can be submitted`}>
                        <Chip size="small" label={row.OpenSuggestions} variant="outlined"
                              sx={{ height: 20, fontSize: 11 }} />
                    </Tooltip>
                )}
                <Box sx={{ flexGrow: 1 }} />
                {/* NO MENU AT ALL when the state offers nothing. A superseded or voided revision is
                    terminal, and a menu of disabled items implies otherwise. */}
                {actions.length > 0 && (
                    <IconButton size="small" aria-label={`Actions for ${row.Name || "document"}`}
                                onClick={(e) => { e.stopPropagation(); setMenu(e.currentTarget); }}>
                        <MoreVert sx={{ fontSize: 18 }} />
                    </IconButton>
                )}
            </Box>

            <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}>
                {actions.map((a) => (
                    <MenuItem key={a.action} disabled={!!busy}
                              onClick={() => { setMenu(null); onAction(a.action); }}>
                        <ListItemIcon sx={{ color: a.destructive ? "#C0392B" : Grey17 }}>{a.icon}</ListItemIcon>
                        <ListItemText slotProps={{ primary: {
                            style: { fontSize: 14, color: a.destructive ? "#C0392B" : Grey17 } } }}>
                            {a.label}
                        </ListItemText>
                    </MenuItem>
                ))}
            </Menu>
        </Card>
    );
}
